import { useCallback } from 'react';
import { Button } from '../../components/atoms/Button';
import { Inline } from '../../components/atoms/Inline';
import { useTranslation } from '../../i18n/useTranslation';

export type TimelineZoomRange = 'month' | 'year' | 'all';

export interface TimelineZoomControlsProps {
  range: TimelineZoomRange;
  onChange: (range: TimelineZoomRange) => void;
  disabled?: boolean;
}

export function TimelineZoomControls({
  range,
  onChange,
  disabled = false,
}: TimelineZoomControlsProps) {
  const { t } = useTranslation();

  const handleRangeChange = useCallback(
    (newRange: TimelineZoomRange) => () => {
      if (!disabled) {
        onChange(newRange);
      }
    },
    [disabled, onChange],
  );

  return (
    <Inline
      role="group"
      aria-label={t('dashboard.timeline.zoom.groupLabel')}
      gap="2"
    >
      <Button
        variant={range === 'month' ? 'secondary' : 'ghost'}
        size="sm"
        aria-pressed={range === 'month'}
        disabled={disabled}
        onClick={handleRangeChange('month')}
      >
        {t('dashboard.timeline.zoom.month')}
      </Button>
      <Button
        variant={range === 'year' ? 'secondary' : 'ghost'}
        size="sm"
        aria-pressed={range === 'year'}
        disabled={disabled}
        onClick={handleRangeChange('year')}
      >
        {t('dashboard.timeline.zoom.year')}
      </Button>
      <Button
        variant={range === 'all' ? 'secondary' : 'ghost'}
        size="sm"
        aria-pressed={range === 'all'}
        disabled={disabled}
        onClick={handleRangeChange('all')}
      >
        {t('dashboard.timeline.zoom.all')}
      </Button>
    </Inline>
  );
}
